import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { Building } from '../../model/building';
import { BuildingCommandHandlerResolver } from '../../service/command/building-command-handler-resolver.service';
import { BuildingExportCommandHandler } from '../../service/command/building-export-command-handler.service';
import { BuildingService } from '../../service/data/building.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { CompanyService } from '../../service/data/company.service';
import { PropertyService } from '../../service/data/property.service';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { CustomPropertyVisibilityType } from '../../model/const/enums';
import { forkJoin } from 'rxjs';
import ListViewColumn from '../../../core/model/list-view-column';
import { ColumnDefinition } from '../../../core/model/column-definition';

@Component({
    selector: 'app-building-manager',
    templateUrl: './building-manager.component.html',
    styleUrls: ['./../../../core/component/multiselect-object-manager/multiselect-object-manager.component.scss'],
    providers: [BuildingCommandHandlerResolver, BuildingExportCommandHandler],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BuildingManagerComponent extends MultiselectObjectManagerComponentBase<Building> {
  menuDefinition: MenuItemDefinition[];
  excelColumnDefinition: ColumnDefinition<Building>[] = [];
  excelContext = 'Building';
  excelFileName = 'Byggnader';
  selectedColumns: ListViewColumn<Building>[] = [];
  selectedBuildingIds: string[] = [];
  private pendingSelectedColumns: ListViewColumn<Building>[] = [];
  private columnUpdateScheduled = false;
  constructor(
    columnDefinitionService: ColumnDefinitionService,
    private _dataService: BuildingService,
    private readonly _companyService: CompanyService,
    private readonly _propertyService: PropertyService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _customPropertyHelper: CustomPropertyHelper,
    dispatcher: CommandDispatcher,
    handlerResolver: BuildingCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService
  ) {
    super(_dataService, dispatcher, handlerResolver, messageService, navigationService);

    this.columnDefinition = columnDefinitionService.create('Building');
    this.title = 'BUILDING_LIST.HEADER';

    this.menuDefinition = [];
    this.excelColumnDefinition = [...this.columnDefinition];
    this.preferencesKey = 'rent.building-manager.list';
  }

  ngOnInit() {
    this.AddSubscription(
      forkJoin([this._dataService.getFilterDefinition(), this._customPropertyService.getList()]).subscribe(([definition, customProperties]) => {
        this.defaultFilters = definition.defaultFields;
        const baseFilters = [
          new FilterDefinitionItem(
            'COMPANY.LIST_HEADER',
            'company',
            definition.company,
            this._companyService,
            definition.selectedFields.includes('company')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
            'property',
            definition.property,
            this._propertyService,
            definition.selectedFields.includes('property')
          ),
        ];

        const customPropertyFilters = this._customPropertyHelper.generateCustomPropertyFilters(
          customProperties,
          definition,
          CustomPropertyVisibilityType.Building
        );
        this.filterDefinition = [...baseFilters, ...customPropertyFilters];
        this._customPropertyHelper.createCustomPropertyColumnDefinition(
          customProperties,
          this.columnDefinition,
          CustomPropertyVisibilityType.Building
        );
        this.columnDefinition = [...this.columnDefinition];
        this.excelColumnDefinition = [...this.columnDefinition];
      })
    );

    this.addEditDeleteCommands();
  }

  override filterInternal(filterState: any) {
    this._customPropertyHelper.applyCustomPropertyFilters(filterState);
  }

  get excelReady(): boolean {
    return this.selectedColumns.length > 0;
  }

  get selectedBuildingCount(): number {
    return this.selectedBuildingIds.length;
  }

  get allBuildingsSelected(): boolean {
    return this.selectedBuildingCount > 0 && this.selectedBuildingCount === this.itemsSource?.length;
  }

  get totalBuildings(): number {
    return this.itemsSource?.length ?? 0;
  }

  onColumnChange(columns: ListViewColumn<Building>[]): void {
    this.pendingSelectedColumns = columns.filter(column => column.selected);
    if (this.columnUpdateScheduled) return;

    this.columnUpdateScheduled = true;
    queueMicrotask(() => {
      this.columnUpdateScheduled = false;
      this.selectedColumns = this.pendingSelectedColumns;
    });
  }

  onSelectedBuildingsChange(buildings: Building[]): void {
    this.selectedBuildingIds = buildings.map(building => building.id);
  }

  override onFilterChange(filterState: any): void {
    this.selectedBuildingIds = [];
    super.onFilterChange(filterState);
  }
}
